import {createContext, KeyboardEvent, ReactNode, useContext, useEffect, useId, useRef} from "react";
import {createPortal} from "react-dom";
import {HiOutlineX} from "react-icons/hi";
import {cn} from "@/lib/utils";

// Replaces flowbite-react's Modal with the same props, parts, classes and behaviour (0.9, not dismissible): only the
// close button closes it, the page behind does not scroll and focus stays inside. flowbite-react needs tailwindcss
// as a runtime peer dependency, and tailwindcss brings braces (GHSA-vfj7-8cjw-p6xm, no fixed version).

interface ModalContextValue {
    popup?: boolean;
    onClose?: () => void;
    headerId: string;
}

const ModalContext = createContext<ModalContextValue | null>(null);

function useModalContext(): ModalContextValue {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error("Modal.Header and Modal.Body must be used inside a Modal");
    }
    return context;
}

const focusable = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalProps {
    show: boolean;
    onClose?: () => void;
    popup?: boolean;
    id?: string;
    children?: ReactNode;
}

function ModalRoot({show, onClose, popup, id, children}: ModalProps) {
    const headerId = useId();
    const dialogRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!show) return;
        const previousFocus = document.activeElement as HTMLElement | null;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        dialogRef.current?.querySelector<HTMLElement>(focusable)?.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
            previousFocus?.focus();
        };
    }, [show]);

    // Keeps Tab and Shift+Tab inside the dialog.
    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== "Tab" || !dialogRef.current) return;
        const elements = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(focusable));
        if (elements.length === 0) return;
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    if (!show) {
        return null;
    }

    return createPortal(
        <ModalContext.Provider value={{popup, onClose, headerId}}>
            <div id={id}
                 className="fixed inset-x-0 top-0 z-50 flex h-screen items-center justify-center overflow-y-auto overflow-x-hidden bg-gray-900 bg-opacity-50 md:inset-0 md:h-full dark:bg-opacity-80">
                <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={headerId} onKeyDown={onKeyDown}
                     className="relative h-full w-full max-w-2xl p-4 md:h-auto">
                    <div className="relative flex max-h-[90dvh] flex-col rounded-lg bg-white shadow dark:bg-gray-700">
                        {children}
                    </div>
                </div>
            </div>
        </ModalContext.Provider>,
        document.body
    );
}

function ModalHeader({children}: { children?: ReactNode }) {
    const {popup, onClose, headerId} = useModalContext();
    return (
        <div className={cn("flex items-start justify-between rounded-t border-b p-5 dark:border-gray-600",
            popup && "border-b-0 p-2")}>
            <h3 id={headerId} className="text-xl font-medium text-gray-900 dark:text-white">{children}</h3>
            <button aria-label="Close" type="button" onClick={onClose}
                    className="ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white">
                <HiOutlineX aria-hidden className="h-5 w-5"/>
            </button>
        </div>
    );
}

function ModalBody({children}: { children?: ReactNode }) {
    const {popup} = useModalContext();
    return <div className={cn("flex-1 overflow-auto p-6", popup && "pt-0")}>{children}</div>;
}

export const Modal = Object.assign(ModalRoot, {Header: ModalHeader, Body: ModalBody});
